import styled from 'styled-components';

import { CONTROL_BORDER, FOCUS_RING } from './base';

// ---------------------------------------------------------------------------
// Régua de Cobrança — anexos da tratativa (handoff 0096).
//
// Duas superfícies distintas, mesmo vocabulário visual:
//  · SELEÇÃO (form de tratativa): área de drop + linhas dos arquivos escolhidos
//    (ainda locais, não enviados) com botão de remover;
//  · LEITURA (item da timeline): chips do que já foi gravado — ativo (baixa) ou
//    excluído (rastro riscado, sem download).
//
// ⚠️ G.4.1: todo styled.button aqui declara o próprio `:hover` COM background —
// sem isso o hover azul do GlobalStyles legado vaza por especificidade
// (`button:hover` 0,0,1,1 vence a classe do styled-component 0,0,1,0).
// ---------------------------------------------------------------------------

// ─── Seleção (form) ────────────────────────────────────────────────────────

export const DropArea = styled.div`
   border: 1px dashed
      ${({ $dragging, theme }) =>
         $dragging ? theme?.colors?.primary ?? '#4098ef' : CONTROL_BORDER};
   border-radius: 10px;
   background: ${({ $dragging }) => ($dragging ? '#f3f8fe' : '#fcfcfd')};
   padding: 12px;
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
   transition: background 120ms ease, border-color 120ms ease;
`;

export const DropHint = styled.div`
   flex: 1;
   min-width: 150px;
   font-size: 11.5px;
   color: #9a9a9a;
   line-height: 1.45;
`;

// Gatilho do input escondido. Ghost neutro: escolher arquivo não escreve nada
// no banco (G.1) — quem leva o azul é o "Registrar" da tratativa.
export const AttachButton = styled.button`
   height: 32px;
   padding: 0 12px;
   border-radius: 8px;
   border: 1px solid ${CONTROL_BORDER};
   background: #fff;
   color: #444;
   font-family: inherit;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   white-space: nowrap;
   transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

   &:hover:not(:disabled) {
      background: #f3f8fe;
      border-color: ${({ theme }) => theme?.colors?.primary ?? '#4098ef'};
      color: #1f66bd;
   }

   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }

   svg {
      flex-shrink: 0;
   }
`;

export const HiddenFileInput = styled.input`
   display: none;
`;

export const SelectedList = styled.ul`
   list-style: none;
   margin: 8px 0 0;
   padding: 0;
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

export const SelectedRow = styled.li`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 6px 8px;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 8px;
   background: #fff;
   font-size: 12px;
   color: #444;

   svg {
      flex-shrink: 0;
      color: #8a8a8a;
   }
`;

export const SelectedName = styled.span`
   flex: 1;
   min-width: 0;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   font-weight: 600;
`;

export const SelectedSize = styled.span`
   font-size: 11px;
   color: #9a9a9a;
   white-space: nowrap;
   font-variant-numeric: tabular-nums;
`;

export const RemoveButton = styled.button`
   width: 24px;
   height: 24px;
   flex: 0 0 24px;
   border-radius: 6px;
   border: 1px solid transparent;
   background: transparent;
   color: #9a9a9a;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-family: inherit;
   transition: background 120ms ease, color 120ms ease, border-color 120ms ease;

   &:hover:not(:disabled) {
      background: rgba(220, 38, 38, 0.08);
      border-color: rgba(220, 38, 38, 0.3);
      color: #a01b1b;
   }

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

// Erro de validação do cliente (mime/tamanho/contagem): inline no card, nunca
// toast — é validação de campo (FC4 B.3).
export const AttachError = styled.div`
   margin-top: 6px;
   font-size: 11.5px;
   font-weight: 600;
   color: #a01b1b;
   line-height: 1.45;
`;

// ─── Leitura (timeline) ────────────────────────────────────────────────────

export const ChipRow = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin-top: 6px;
`;

// Moldura do chip: contém DOIS botões (baixar / excluir), por isso é um span —
// botão dentro de botão é HTML inválido.
export const Chip = styled.span`
   display: inline-flex;
   align-items: stretch;
   max-width: 100%;
   border: 1px solid ${CONTROL_BORDER};
   border-radius: 999px;
   background: #fff;
   overflow: hidden;
`;

export const ChipMain = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   min-width: 0;
   max-width: 100%;
   height: 26px;
   padding: 0 10px;
   border: none;
   background: transparent;
   color: #1f66bd;
   font-family: inherit;
   font-size: 11.5px;
   font-weight: 700;
   cursor: pointer;
   transition: background 120ms ease, color 120ms ease;

   &:hover:not(:disabled) {
      background: #f3f8fe;
   }

   &:disabled {
      cursor: progress;
      opacity: 0.6;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }

   svg {
      flex-shrink: 0;
   }
`;

export const ChipName = styled.span`
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   max-width: 190px;
`;

export const ChipSize = styled.span`
   font-weight: 600;
   color: #9a9a9a;
   white-space: nowrap;
   font-variant-numeric: tabular-nums;
`;

export const ChipDelete = styled.button`
   width: 26px;
   height: 26px;
   flex: 0 0 26px;
   border: none;
   border-left: 1px solid ${CONTROL_BORDER};
   background: transparent;
   color: #9a9a9a;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   font-family: inherit;
   transition: background 120ms ease, color 120ms ease;

   &:hover:not(:disabled) {
      background: rgba(220, 38, 38, 0.08);
      color: #a01b1b;
   }

   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }

   &:focus-visible {
      outline: none;
      box-shadow: ${FOCUS_RING};
   }
`;

// Anexo excluído: rastro, não item operável. Riscado, esmaecido, sem download
// (o tooltip carrega quem excluiu, quando e por quê).
export const ChipDeleted = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   max-width: 100%;
   height: 26px;
   padding: 0 10px;
   border: 1px dashed rgba(0, 0, 0, 0.18);
   border-radius: 999px;
   background: rgba(0, 0, 0, 0.03);
   color: #9a9a9a;
   font-size: 11.5px;
   font-weight: 700;
   cursor: help;

   svg {
      flex-shrink: 0;
   }
`;

export const ChipDeletedName = styled.span`
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   max-width: 190px;
   text-decoration: line-through;
`;
